import { apiClient } from '@/lib/apiClient'
import type { ApiResponse } from '@/types/api.types'
import type {
  ConversationSummary, Message, MessageScope, MessagesPage, UnreadCount,
} from '@/types/message.types'

/**
 * Lo scope `peer` (chat fra colleghi) viaggia sulle stesse rotte della chat
 * clienti: `?scope=peer` sulle GET di lista, `scope` nel body del POST di
 * creazione. Le rotte per-conversazione lo deducono dal prefisso `peer_` del
 * convId, quindi non hanno bisogno di parametri.
 */
const scopeParams = (scope: MessageScope) => (scope === 'peer' ? { scope: 'peer' } : {})

/** Namespace React Query separato per scope: le invalidazioni non si incrociano. */
export const messagesQueryKey = (scope: MessageScope, ...rest: string[]) =>
  [scope === 'peer' ? 'peer-messages' : 'messages', ...rest]

/** Lista conversazioni del role corrente, ordinate per lastMessageAt DESC. */
export async function listMyConversations(scope: MessageScope = 'client'): Promise<ConversationSummary[]> {
  const { data } = await apiClient.get<ApiResponse<{ items: ConversationSummary[] }>>(
    '/me/conversations',
    { params: scopeParams(scope) },
  )
  return data.data.items
}

/** Conta i messaggi non letti totali (per il badge sidebar). */
export async function getUnreadCount(scope: MessageScope = 'client'): Promise<number> {
  const { data } = await apiClient.get<ApiResponse<UnreadCount>>(
    '/me/conversations/unread-count',
    { params: scopeParams(scope) },
  )
  return data.data.count
}

export interface Contact {
  userId:            string
  name:              string
  avatarUrl:         string
  lastInteractionAt: string
  /** Solo scope `peer`: profilo pubblico e riga descrittiva del collega. */
  slug?:             string
  specialtyId?:      string
  city?:             string
  /** `SUPPORT` per la casella condivisa «Supporto OLLYlife». */
  kind?:             'SUPPORT'
  /** Solo lato admin: status del profilo operatore. */
  status?:           string
  suspended?:        boolean
}

/** Controparti contattabili (per dialog "Nuova conversazione").
 *  `client`: chi ha una prestazione pagata e completata; `peer`: tutti i colleghi pubblicati. */
export async function listContacts(scope: MessageScope = 'client'): Promise<Contact[]> {
  const { data } = await apiClient.get<ApiResponse<{ items: Contact[] }>>(
    '/me/conversations/contacts',
    { params: scopeParams(scope) },
  )
  return data.data.items
}

/** Crea una nuova conversazione + primo messaggio (verifica gating server-side). */
export async function createConversation(
  params: { otherUserId: string; body: string },
  scope: MessageScope = 'client',
): Promise<{ convId: string; message: Message }> {
  const { data } = await apiClient.post<ApiResponse<{ convId: string; message: Message }>>(
    '/me/conversations',
    { ...params, ...scopeParams(scope) },
  )
  return data.data
}

/** Storico messaggi paginato (ASC). Per pagina precedente passare `before`. */
export async function listMessages(
  convId: string,
  params: { before?: string; limit?: number } = {},
): Promise<MessagesPage> {
  const { data } = await apiClient.get<ApiResponse<MessagesPage>>(
    `/me/conversations/${encodeURIComponent(convId)}/messages`,
    { params },
  )
  return data.data
}

/** Invia un nuovo messaggio in una conversazione esistente. */
export async function sendMessage(convId: string, body: string): Promise<Message> {
  const { data } = await apiClient.post<ApiResponse<{ message: Message }>>(
    `/me/conversations/${encodeURIComponent(convId)}/messages`,
    { body },
  )
  return data.data.message
}

/** Mark-as-read della conversazione per il chiamante. */
export async function markConversationRead(convId: string): Promise<void> {
  await apiClient.post(`/me/conversations/${encodeURIComponent(convId)}/read`)
}
